import { useEffect, useRef, useState } from "react";
import { useRoute, useLocation } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { useI18n } from "@/lib/i18n";
import { getGoogleSignInUrl } from "@/const";
import { isNative, nativeGoogleSignIn } from "@/native";
import { Loader2, Building2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Org-invite acceptance route (/org-invite/:token).
 *
 * Org invites get a confirmation screen: "Join {OrgName}" plus an explicit
 * Accept button. Not logged in → bounce to OAuth, return here.
 *
 * This page has ALWAYS required a deliberate press; it never accepted on
 * mount. The team page did, which is half of what made A0-P1 exploitable —
 * see the note in `InviteAccept.tsx`. It has been brought in line with this
 * one. Nothing here may regress to accepting on mount; that is pinned by
 * `client/src/pages/inviteExplicitAcceptance.test.tsx`.
 *
 * After accept the user is in the org but on NO team yet; we land
 * them on the dashboard with a toast telling them an admin will
 * assign teams.
 */
export default function OrgInviteAccept() {
  const { isAuthenticated, loading, user } = useAuth();
  const [, params] = useRoute<{ token: string }>("/org-invite/:token");
  const [, navigate] = useLocation();
  const { locale } = useI18n();
  const token = params?.token ?? "";
  const infoQ = trpc.org.inviteInfo.useQuery(
    { token },
    { enabled: isAuthenticated && !!token, retry: false },
  );
  const accept = trpc.org.acceptInvite.useMutation();
  const ranRef = useRef(false);
  const [done, setDone] = useState(false);
  const [alreadyMember, setAlreadyMember] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      // See InviteAccept for why `getLoginUrl()` was wrong here.
      const target = getGoogleSignInUrl(token ? { kind: "org", token } : undefined);
      void isNative().then(native => {
        if (native) void nativeGoogleSignIn();
        else window.location.href = target;
      });
    }
  }, [loading, isAuthenticated]);

  if (loading || !isAuthenticated) {
    return (
      <div className="min-h-screen grid place-items-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  function doAccept() {
    if (ranRef.current) return;
    ranRef.current = true;
    accept.mutate(
      { token },
      {
        onSuccess: r => {
          setDone(true);
          if ((r as any).alreadyMember) {
            setAlreadyMember(true);
            toast.info(
              locale === "ar"
                ? "أنت عضو في هذه المؤسسة بالفعل."
                : "You're already a member of this organization.",
            );
          } else {
            toast.success(
              locale === "ar"
                ? "انضممت إلى المؤسسة"
                : "You've joined the organization",
            );
          }
          setTimeout(() => navigate("/"), 2500);
        },
        onError: e => {
          ranRef.current = false;
          toast.error(e.message);
        },
      },
    );
  }

  const invalid =
    infoQ.data && infoQ.data.ok === false ? infoQ.data.reason : null;

  return (
    <div className="min-h-screen grid place-items-center bg-background px-4">
      <div className="max-w-md w-full rounded-2xl border bg-card p-8 text-center">
        <div className="mx-auto mb-4 h-12 w-12 rounded-xl bg-coral grid place-items-center">
          <Building2 className="h-6 w-6 text-white" />
        </div>

        {infoQ.isLoading && (
          <div className="flex items-center justify-center gap-2 text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin" />
            {locale === "ar" ? "جاري التحقق…" : "Checking invitation…"}
          </div>
        )}

        {invalid && (
          <>
            <AlertCircle className="h-5 w-5 text-red-600 mx-auto mb-2" />
            <div className="font-medium mb-1">
              {locale === "ar"
                ? "دعوة غير صالحة"
                : "Invitation not valid"}
            </div>
            <p className="text-sm text-muted-foreground">
              {invalid === "expired"
                ? locale === "ar"
                  ? "انتهت صلاحية هذه الدعوة."
                  : "This invitation has expired."
                : invalid === "revoked"
                  ? locale === "ar"
                    ? "تمّ إلغاء هذه الدعوة."
                    : "This invitation was revoked."
                  : locale === "ar"
                    ? "لم يتم العثور على هذه الدعوة."
                    : "This invitation was not found."}
            </p>
            <Button
              variant="outline"
              className="mt-5"
              onClick={() => navigate("/")}
            >
              {locale === "ar" ? "إلى لوحة التحكم" : "Go to dashboard"}
            </Button>
          </>
        )}

        {done && alreadyMember && (
          <>
            <AlertCircle className="h-6 w-6 text-amber-500 mx-auto mb-2" />
            <div className="font-medium">
              {locale === "ar"
                ? "أنت عضو بالفعل"
                : "You're already a member"}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {locale === "ar"
                ? "هذا الحساب منضم لهذه المؤسسة من قبل. إن كانت الدعوة لشخص آخر، افتح الرابط بعد تسجيل الدخول بالحساب المدعو."
                : "This account is already in the organization. If the invite was for someone else, open the link while signed in as the invited account."}
            </p>
          </>
        )}

        {done && !alreadyMember && (
          <>
            <CheckCircle2 className="h-6 w-6 text-emerald-500 mx-auto mb-2" />
            <div className="font-medium">
              {locale === "ar" ? "تمّ الانضمام!" : "You're in!"}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {locale === "ar"
                ? "سيقوم المدير بإضافتك إلى الفرق."
                : "An admin will assign you to teams shortly."}
            </p>
          </>
        )}

        {infoQ.data && infoQ.data.ok === true && !done && (
          <>
            <h1 className="font-display text-xl mb-1">
              {locale === "ar" ? "انضم إلى" : "Join"}{" "}
              <span className="text-coral">{infoQ.data.orgName}</span>
            </h1>
            {/* WHO is accepting — the #1 failure mode is opening the link
                in a window signed in as the wrong (admin) account. */}
            <div className="mb-3 rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              {locale === "ar" ? "ستنضم بحساب:" : "You'll join as:"}{" "}
              <span className="font-medium text-foreground">
                {user?.email ?? user?.name ?? "—"}
              </span>
              <div className="mt-0.5">
                {locale === "ar"
                  ? "ليس الحساب الصحيح؟ سجّل الخروج وافتح الرابط بالحساب المدعو."
                  : "Not the right account? Sign out and open this link as the invited account."}
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-1">
              {locale === "ar"
                ? `ستنضم كـ ${infoQ.data.role === "admin" ? "مدير" : "عضو"}.`
                : `You'll join as ${infoQ.data.role === "admin" ? "an admin" : "a member"}.`}
            </p>
            <p className="text-[12px] text-muted-foreground mb-5">
              {locale === "ar"
                ? "بعد الانضمام سيقوم المدير بإضافتك إلى الفرق المناسبة."
                : "After joining, an admin assigns you to the right teams."}
            </p>
            <Button
              onClick={doAccept}
              disabled={accept.isPending}
              className="w-full bg-coral hover:bg-coral/90 text-white gap-2"
            >
              {accept.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              {locale === "ar" ? "قبول الدعوة" : "Accept invitation"}
            </Button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="mt-3 text-xs text-muted-foreground hover:text-foreground"
            >
              {locale === "ar" ? "ليس الآن" : "Not now"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
